import React, { useState } from 'react';
import { DatePicker, Input, Select, Table } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  approveReqDonateAPI,
  rejectReqDonateAPI,
} from '../../../../api/admin/req-donate.api';
import { promptTotp } from '../../../../utils/admin-stepup';
import toast from 'react-hot-toast';
import { useReqAllDonateHook } from '../../../../hooks/admin/req-donate/use-all-donate';
import dayjs from 'dayjs';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import { ekycErrorMessage } from '../../../donate-goal/ekyc-flow';
import RejectReasonDialog from '../../components/request-review/reject-reason-dialog';
import EligibilityDialog from '../../components/request-review/eligibility-dialog';
import { useApprovalFlow } from '../../components/request-review/use-approval-flow';
import {
  compareRequestStatus,
  normalizeRequestStatus,
  successText,
} from '../../components/request-review/review-rules';

const ManagementRequestDonate = (props) => {
  const [isRefetch, setIsRefetch] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<{
    fullName?: string;
    status?: number;
    bankName?: string;
    requestedDateTo?: number;
    requestedDateFrom?: number;
    repliedDateTo?: number;
    repliedDateFrom?: number;
  }>({});
  const { data, total, loading } = useReqAllDonateHook({
    pageNumber,
    pageSize,
    filters,
    isRefetch,
  });
  const { Option } = Select;
  const { RangePicker } = DatePicker;
  const handleTableChange = (pagination: any) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);
  };
  const handleFilterChange = (
    key: string,
    value: string | number | undefined | boolean
  ) => {
    setPageNumber(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Duyệt: xem điều kiện trước. Thiếu mục bắt buộc thì không duyệt được, thiếu mục xét duyệt thì phải ghi lý do ngoại lệ.
  const doApprove = async (userId: string, note?: string) => {
    const totp = promptTotp('duyệt mở donate');
    if (!totp) return;
    try {
      const res = await approveReqDonateAPI(userId, totp, note);
      setIsRefetch((v) => !v);
      toast.success(successText(res, 'Đã duyệt yêu cầu mở nhận donate.'));
    } catch (error: any) {
      // Máy chủ trả lý do bằng tiếng Việt (thiếu điều kiện, cần ghi lý do, sai mã Authenticator...).
      toast.error(ekycErrorMessage(error, 'Duyệt thất bại'));
    }
  };
  const approval = useApprovalFlow('donate', doApprove);

  // Từ chối: chọn lý do có sẵn, "Lý do khác" phải ghi thêm. Người dùng nhận thông báo kèm lý do.
  const [rejectUserId, setRejectUserId] = useState<string | null>(null);
  const handleReject = async (reasonCode: string, reasonText: string) => {
    if (!rejectUserId) return;
    try {
      const res = await rejectReqDonateAPI(rejectUserId, reasonCode, reasonText);
      setRejectUserId(null);
      setIsRefetch((v) => !v);
      toast.success(successText(res, 'Đã từ chối yêu cầu mở nhận donate.'));
    } catch (error) {
      toast.error(ekycErrorMessage(error, 'Từ chối thất bại'));
      throw error;
    }
  };

  const [eligibilityOf, setEligibilityOf] = useState<{ userId: string; name?: string } | null>(
    null
  );

  const handleDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setFilters((prev) => ({
        ...prev,
        requestedDateFrom: dates[0]?.startOf('day').toISOString(),
        requestedDateTo: dates[1]?.endOf('day').toISOString(),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        requestedDateFrom: undefined,
        requestedDateTo: undefined,
      }));
    }
  };

  const handleDateChangeRep = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setFilters((prev) => ({
        ...prev,
        repliedDateFrom: dates[0]?.startOf('day').toISOString(),
        repliedDateTo: dates[1]?.endOf('day').toISOString(),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        repliedDateFrom: undefined,
        repliedDateTo: undefined,
      }));
    }
  };
  const navigator = useNavigate();
  const columns = [
    {
      title: 'Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <div
          onClick={() => {
            navigator(`/user/${record.userName}`);
          }}
          className="adm-link cursor-pointer"
        >
          {record?.fullName}
        </div>
      ),
    },
    {
      title: 'Ngân hàng',
      dataIndex: 'bankName',
      key: 'bankName',
      render: (text, record) => (
        <div>
          <div>{record.bankName}</div>
          <div>{record.bankAccount}</div>
        </div>
      ),
    },
    {
      title: 'Link Reference',
      dataIndex: 'referenceLinks',
      key: 'referenceLinks',
      render: (text, record) => (
        <Link to={record.referenceLinks} target="_blank">
          {record.referenceLinks}
        </Link>
      ),
    },
    {
      title: 'Thời gian yêu cầu',
      dataIndex: 'referenceLinks',
      key: 'referenceLinks',
      render: (text, record) => (
        <span>{dayjs(record?.requestedDate).format('HH:mm DD-MM-YYYY')}</span>
      ),
    },
    {
      title: 'Thời gian duyệt/ từ chối',
      dataIndex: 'referenceLinks',
      key: 'referenceLinks',
      render: (text, record) => (
        <span>
          {record?.repliedDate
            ? dayjs(record?.repliedDate).format('HH:mm DD-MM-YYYY')
            : 'Chưa phản hồi!'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => compareRequestStatus(a.status, b.status),
      render: (text, record) => {
        const status = normalizeRequestStatus(record.status);
        if (status === 'pending') {
          return <AdminStatusPill tone="warning">Đang chờ duyệt</AdminStatusPill>;
        }
        if (status === 'approved') {
          return <AdminStatusPill tone="success">Yêu cầu được duyệt</AdminStatusPill>;
        }
        if (status === 'rejected') {
          return (
            <div className="flex max-w-[260px] flex-col items-start gap-1">
              <AdminStatusPill tone="danger">Từ chối</AdminStatusPill>
              {record.rejectReason && (
                <span className="whitespace-normal text-xs opacity-80">{record.rejectReason}</span>
              )}
            </div>
          );
        }
        return <AdminStatusPill tone="neutral">Không rõ</AdminStatusPill>;
      },
    },
    {
      title: 'Thao tác',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => (
        <div className="flex gap-2">
          <AdminButton
            variant="outline"
            size={32}
            onClick={() =>
              setEligibilityOf({ userId: record?.userId, name: record?.fullName })
            }
          >
            Điều kiện
          </AdminButton>
          {normalizeRequestStatus(record.status) === 'pending' && (
            <>
              <AdminButton
                variant="success"
                size={32}
                onClick={() => approval.start(record?.userId)}
              >
                Duyệt
              </AdminButton>
              <AdminButton
                variant="danger-soft"
                size={32}
                onClick={() => setRejectUserId(record?.userId)}
              >
                Từ chối
              </AdminButton>
            </>
          )}
        </div>
      ),
    },
  ];
  return (
    <div className="adm-page">
      <AdminPageHeader title="Yêu cầu mở donate cho user" />
      <AdminCard title="Bộ lọc tìm kiếm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="adm-field">
            <span>Tên</span>
            <Input
              className="w-full"
              placeholder="Tìm theo tên"
              value={filters.fullName || ''}
              onChange={(e) => handleFilterChange('fullName', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <span>Ngân hàng</span>
            <Input
              className="w-full"
              placeholder="Tìm theo tên"
              value={filters.bankName || ''}
              onChange={(e) => handleFilterChange('bankName', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <span>
              Thời gian yêu cầu
            </span>
            <RangePicker
              className="w-full"
              onChange={handleDateChange}
              value={
                filters.requestedDateFrom && filters.requestedDateTo
                  ? [
                      dayjs(filters.requestedDateFrom),
                      dayjs(filters.requestedDateTo),
                    ]
                  : null
              }
            />
          </div>
          <div className="adm-field">
            <span>
              Thời gian xác nhận
            </span>
            <RangePicker
              className="w-full"
              onChange={handleDateChangeRep}
              value={
                filters.repliedDateFrom && filters.repliedDateTo
                  ? [
                      dayjs(filters.repliedDateFrom),
                      dayjs(filters.repliedDateTo),
                    ]
                  : null
              }
            />
          </div>
          <div className="adm-field">
            <span>Trạng thái</span>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              className="w-full"
            >
              <Option value={0}>Đang chờ duyệt</Option>
              <Option value={-1}>Yêu cầu bị từ chối</Option>
              <Option value={1}>Yêu cầu được duyệt</Option>
            </Select>
          </div>
        </div>
      </AdminCard>
      <AdminCard flush>
        <Table
          scroll={{ x: 'max-content' }}
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={{
            current: pageNumber,
            pageSize,
            total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </AdminCard>
      {approval.dialog}
      <RejectReasonDialog
        open={!!rejectUserId}
        title="Từ chối yêu cầu mở nhận donate"
        onCancel={() => setRejectUserId(null)}
        onSubmit={handleReject}
      />
      <EligibilityDialog
        kind="donate"
        userId={eligibilityOf?.userId ?? null}
        name={eligibilityOf?.name}
        onClose={() => setEligibilityOf(null)}
      />
    </div>
  );
};

export default ManagementRequestDonate;
